<template>
  <div class="formItem formItem--1x4">
    <v-text-field
      v-model="address.street_and_number"
      :placeholder="t('form.placeholders.street_and_number_ph')"
      :label="t('form.common.street')"
      :error-messages="get(errorMessages, 'street_and_number')"
      :rules="defaultRules"
      :disabled="disabled"
      name="address_street_and_number"
      @input="emit('errorReset','street_and_number')"
      @change="emit('change')"
    />
  </div>
  <div class="formItem formItem--1x4">
    <v-text-field
      v-model="address.postal_code"
      :placeholder="t('form.placeholders.post_code_ph')"
      :label="t('form.common.postcode')"
      :error-messages="get(errorMessages, 'postal_code')"
      :rules="defaultRules.concat([rules.postCode])"
      :disabled="disabled"
      name="address_postal_code"
      @input="emit('errorReset', 'postal_code')"
      @change="emit('change')"
    />
  </div>
  <div class="formItem formItem--1x4">
    <v-text-field
      v-model="address.city"
      :placeholder="t('form.placeholders.city_ph')"
      :label="t('form.common.city')"
      :error-messages="get(errorMessages, 'city')"
      :rules="defaultRules"
      :disabled="disabled"
      name="address_city"
      @input="emit('errorReset', 'city')"
      @change="emit('change')"
    />
  </div>
  <div class="formItem formItem--1x12">
    <v-autocomplete
      v-model="address.country"
      :label="t('form.common.country')"
      :error-messages="get(errorMessages, 'country_of_birth') || get(errorMessages, 'country')"
      :items="countryChoices"
      item-title="short_name"
      item-value="id"
      :loading="countriesLoading"
      :disabled="countriesLoading || props.isPickupAddress || disabled"
      :rules="defaultRules"
      name="address_country"
      @update:model="emit('errorReset','country_of_birth')"
      @change="emit('change')"
    />
  </div>
</template>
<script setup lang="ts">
import { get } from "lodash-es";
import { storeToRefs } from "pinia";

const countryStore = useCountryStore();
const { countryChoices, countriesLoading } = storeToRefs(countryStore);
const { rules } = useFormValidators();
const config = useRuntimeConfig();
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    modelValue?: PostalAddress,
    errorMessages?: AddressFormErrorMessages,
    required?: boolean,
    isPickupAddress?: boolean,
    disabled?: boolean
  }>(),
  {
    modelValue: () => ({
      street_and_number: "",
      postal_code: "",
      city: "",
      country: null
    }),
    errorMessages: () => ({
      street_and_number: [],
      postal_code: [],
      city: [],
      country_of_birth: []
    }),
    required: false,
    isPickupAddress: false,
    disabled: false
  }
);

const emit = defineEmits(["update:modelValue", "errorReset", "change"]);

onMounted(async () => {
  await countryStore.loadCountryChoicesData();
  if (props.isPickupAddress) {
    // @ts-ignore
    address.value.country = (countryChoices.value.find(cCh => cCh.iso_identifier.toLowerCase() === config.public.region)) ?? null;
  }
});

const address = computed({
  get () {
    return props.modelValue;
  },
  set (value) {
    emit("update:modelValue", value);
  }
});

if (address.value.country && typeof address.value.country !== "string") {
  // we always want the country to be a UUID instead of object (for saving)
  const id = address.value.country?.id;
  if (!countriesLoading.value) {
    // @ts-ignore
    address.value.country = id;
  } else {
    watch(() => countriesLoading.value, (loading) => {
      if (!loading) {
        // @ts-ignore
        address.value.country = id;
      }
    });
  }
}

const defaultRules = computed(() => props.required ? [rules.required] : []);

</script>
